import { useDispatch, useSelector } from 'react-redux';
import EditorPublishButton from 'ContentEditorUI/components/shared/EditorPublishButton';
import { FloatingAlertStore } from 'foundations-components/transitional/components/FloatingAlertList';
import FormattedMessage from 'I18n/components/FormattedMessage';
import { selectActiveHubDbRowId } from 'structured-content-lib/redux/selectors/instanceEditSelectors';
import usePublishActionHandlers, { useCMPPublishActionHandlers } from 'ContentEditorUI/components/publishing/hooks/usePublishActionHandlers';
import { updateInstanceValues, fetchInstanceData } from 'structured-content-lib/redux/actions/instanceEditActions';
// @ts-expect-error not typed
import { showPublishErrors } from 'ContentEditorUI/redux/actions/publishActions';
import { getCategoryId } from 'ContentEditorUI/redux/selectors/contentReadOnlyDataSelectors';
import { selectInstanceWarningLabels, selectInstanceErrorLabels, selectIsInstancePublished, selectButtonTextKey } from '../../data/editor/publish';
import { fetchInstance, publishInstance, unpublishInstance } from '../../api';
import { save as saveAction } from '../../data/editor/save';
import { updateFocusedItem } from '../../data/editor';
import { hasPublisherPermission } from 'case-study-lib/utils/auth';
import { ContentObjectTypes } from 'content-management-data-lib';
import { selectIsUngatedForCMPPublicationAPIs } from '../../data/auth';
import { OnboardingTourStepPublish } from './OnboardingTour';
import { displayPublishSuccessAlert } from 'ContentEditorUI/utils/displayPublishSuccessAlert';
import { PublishTypes } from 'ContentEditorUI/constants/PublishTypes';
import { selectCaseStudyUrl } from '../../data/editor/instance';
import { getActiveDomainIsConnected, getActiveDomainIsInternal } from 'ContentEditorUI/redux/selectors/portalDomainsSelectors';
// @ts-expect-error not typed
import { trackPublish } from 'ContentEditorUI/redux/actions/publishActions';
import { CONTENT_TYPE_TABLE_NAME } from '../../constants';
// @ts-expect-error not typed
import { onFetchContentSchemaFailed } from 'ContentEditorUI/redux/actions/appActions';
import { jsx as _jsx } from "react/jsx-runtime";
const usePublishUnpublishActionHandlers = () => {
  const caseStudyId = useSelector(selectActiveHubDbRowId);
  const dispatch = useDispatch();
  const categoryId = useSelector(getCategoryId);
  const appState = useSelector(state => state);
  const shouldUseCmpPublishHandler = useSelector(selectIsUngatedForCMPPublicationAPIs);
  const isPublished = useSelector(selectIsInstancePublished);
  const url = useSelector(selectCaseStudyUrl);
  const domainIsConnected = useSelector(getActiveDomainIsConnected);
  const domainIsInternal = useSelector(getActiveDomainIsInternal);
  const isConnectedAndCustomDomain = domainIsConnected && !domainIsInternal;
  const scpPublishType = shouldUseCmpPublishHandler ? undefined : isPublished ? PublishTypes.update : PublishTypes.publish;
  const refetchInstance = () => {
    dispatch(fetchInstanceData(CONTENT_TYPE_TABLE_NAME, () => fetchInstance(caseStudyId))).catch(result => dispatch(onFetchContentSchemaFailed({
      result
    })));
  };
  const onPublishSuccess = (data, publishType) => {
    if (shouldUseCmpPublishHandler) {
      refetchInstance();
    } else {
      dispatch(updateInstanceValues(data));
    }
    displayPublishSuccessAlert({
      contentType: 'caseStudy',
      publishType: scpPublishType || publishType || PublishTypes.publish,
      publishedContentUrl: url,
      canScheduleWithContentCrmObject: shouldUseCmpPublishHandler
    });
  };
  const onUnpublishSuccess = data => {
    FloatingAlertStore.addAlert({
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unpublishModal.success.message"
      }),
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unpublishModal.success.text"
      }),
      type: 'success'
    });
    if (shouldUseCmpPublishHandler) {
      refetchInstance();
    } else {
      dispatch(updateInstanceValues(data));
    }
  };
  const onPublishError = errorResponse => {
    FloatingAlertStore.addAlert({
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "publishModal.publishFailed"
      }),
      type: 'danger'
    });
    showPublishErrors(errorResponse.responseJSON || {}, categoryId, appState);
  };
  const onUnpublishError = errorResponse => {
    FloatingAlertStore.addAlert({
      message: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unpublishModal.error.message"
      }),
      titleText: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "unpublishModal.error.text"
      }),
      type: 'danger'
    });
    showPublishErrors(errorResponse.responseJSON || {}, categoryId, appState);
  };
  const publishUnpublishAPICallback = isUnpublish => {
    return isUnpublish ? unpublishInstance(caseStudyId) : publishInstance(caseStudyId);
  };
  const trackPublishEvent = (isSuccessful, publishType) => {
    trackPublish({
      contentId: caseStudyId,
      isConnectedAndCustomDomain,
      publishType,
      isSuccessful
    });
  };
  const publishActionProps = {
    saveAction,
    onPublishSuccess,
    onPublishError,
    onUnpublishSuccess,
    onUnpublishError,
    trackPublishEvent
  };
  const {
    publish,
    unpublish,
    schedulePublishUnpublish
  } = useCMPPublishActionHandlers(Object.assign({}, publishActionProps, {
    contentObjectType: ContentObjectTypes.CASE_STUDY,
    objectId: caseStudyId
  }));
  const publishActionHandlers = usePublishActionHandlers(Object.assign({}, publishActionProps, {
    publishUnpublishAPICallback
  }));
  return shouldUseCmpPublishHandler ? {
    publish,
    unpublish,
    schedulePublishUnpublish
  } : Object.assign({}, publishActionHandlers);
};
const CaseStudyPublishButtonContainer = () => {
  const errors = useSelector(selectInstanceErrorLabels);
  const warnings = useSelector(selectInstanceWarningLabels);
  const publishEnabled = hasPublisherPermission();
  const isPublished = useSelector(selectIsInstancePublished);
  const buttonTextKey = useSelector(selectButtonTextKey);
  const shouldUseCmpPublishHandler = useSelector(selectIsUngatedForCMPPublicationAPIs);
  const {
    publish,
    unpublish,
    schedulePublishUnpublish
  } = usePublishUnpublishActionHandlers();
  return /*#__PURE__*/_jsx(OnboardingTourStepPublish, {
    children: /*#__PURE__*/_jsx(EditorPublishButton, {
      errors: errors,
      warnings: warnings,
      canSchedule: shouldUseCmpPublishHandler,
      publishEnabled: publishEnabled,
      unpublishEnabled: isPublished,
      buttonTextKey: buttonTextKey,
      updateFocusedItemAction: updateFocusedItem,
      publish: publish,
      unpublish: unpublish,
      schedulePublishUnpublish: schedulePublishUnpublish,
      schedulingOptionsModalProps: {
        canScheduleUnpublish: shouldUseCmpPublishHandler
      },
      isNotAuthorizedToPublish: !publishEnabled
    })
  });
};
export default CaseStudyPublishButtonContainer;